
import React, { useState, useEffect } from 'react';
import { ref, onValue, off, remove, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../firebase';
import ReceiptManagementSystem from './Receivables'; // Adjust the import path as needed
import { Edit3, Trash2, DollarSign } from 'lucide-react'; // Import required icons

const baseButton = {
  padding: '0.875rem 2rem',
  borderRadius: '8px',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer',
  border: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
  transform: 'translateY(0)',
};

const CustomerTable = ({ onAddCustomer, onEditCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiptPage, setShowReceiptPage] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    if (!currentUser || customers.length === 0) return;
  
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const salesRef = ref(database, `users/${encodedEmail}/sales`);
    
    onValue(salesRef, snapshot => {
      const salesData = snapshot.val() || {};
      setCustomers(prevCustomers => prevCustomers.map(customer => {
        const customerSales = Object.values(salesData).filter(
          sale => (sale.customer && (sale.customer === customer.customerName || sale.customer === customer.name))
        );
        
        // Calculate total outstanding from all bills for this customer
        const totalOutstanding = customerSales.reduce((total, sale) => {
          const outstanding = sale.summary?.outstanding || 0;
          return total + Number(outstanding);
        }, 0);
        
        // Create a summary object with the total outstanding
        const summary = {
          outstanding: totalOutstanding
        };
        
        return {
          ...customer,
          summary,
        };
      }));
    });
  }, [currentUser, customers.length]);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setCustomers([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  
  useEffect(() => {
    if (!currentUser) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const customersRef = ref(database, `users/${encodedEmail}/customers`);

    const handleData = (snapshot) => {
      const customersData = [];
      snapshot.forEach((childSnapshot) => {
        const customerData = childSnapshot.val();
        customersData.push({
          id: childSnapshot.key,
          ...customerData,
          customerName: customerData.customerName || customerData.name || '',
          type: customerData.under || customerData.type || 'N/A', // Map 'under' to 'type'
          mobile: customerData.mobileNo || customerData.mobile || 'N/A', // Map 'mobileNo' to 'mobile'
          gstin: customerData.gstTinNo || customerData.gstin || 'N/A', // Map 'gstTinNo' to 'gstin'
        });
      });
      setCustomers(customersData);
      setLoading(false);
    };

    const handleError = (error) => {
      console.error("Error fetching customers:", error);
      setLoading(false);
    };

    onValue(customersRef, handleData, handleError);

    return () => {
      off(customersRef, 'value', handleData);
    };
  }, [currentUser]);

  const handleDelete = async (customerId) => {
    if (!currentUser) return;
    if (window.confirm("Are you sure you want to delete this customer?")) {
      try {
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const customerRef = ref(database, `users/${encodedEmail}/customers/${customerId}`);
        await remove(customerRef);
  
        // ✅ Success message
        alert("Customer deleted Successfully");
      } catch (error) {
        console.error("Error deleting customer:", error);
        alert("Failed to delete customer. Please try again."); // Optional error alert
      }
    }
  };
  

  const handleActiveChange = (customerId, newValue) => {
    if (!currentUser) return;
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const customerRef = ref(database, `users/${encodedEmail}/customers/${customerId}`);
    update(customerRef, { visibility: newValue === 'YES' })
      .then(() => {
        setCustomers(customers.map(customer =>
          customer.id === customerId ? { ...customer, visibility: newValue === 'YES' } : customer
        ));
      })
      .catch(error => console.error("Error updating active status:", error));
  };

  const handleCashClick = (customer) => {
    setSelectedCustomer(customer);
    setShowReceiptPage(true);
  };

const exportToCSV = () => {
  const headers = ['S.No', 'Customer Name', 'Type', 'Address', 'City', 'Mobile No', 'GSTIN', 'Outstanding', 'Active'];
  const data = customers.map((customer, index) => [
    index + 1,
    `"${(customer.customerName || customer.name || 'N/A').replace(/"/g, '""')}"`,
    `"${(customer.type || 'N/A').replace(/"/g, '""')}"`,
    `"${(`${customer.address || customer.address1 || ''} ${customer.address2 || ''}`.trim() || 'N/A').replace(/"/g, '""')}"`,
    `"${(customer.city || 'N/A').replace(/"/g, '""')}"`,
    `"${(customer.mobile || 'N/A').replace(/"/g, '""')}"`,
    `"${(customer.gstin || 'N/A').replace(/"/g, '""')}"`,
    Number(customer.summary?.outstanding ?? 0).toFixed(2),
    customer.visibility ? 'YES' : 'NO'
  ]);

  const csvContent = [
    headers.join(','),
    ...data.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'customers_data.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

  const filteredCustomers = customers.filter(customer =>
    (customer.customerName || customer.name || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.type || customer.under || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.address || customer.address1 || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.address2 || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.city || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.mobile || customer.mobileNo || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.gstin || customer.gstTinNo || '')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.email || '')?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      ...baseButton,
      backgroundColor: '#2563eb',
    },
    exportButton: {
      ...baseButton,
      backgroundColor: '#4caf50',
      marginRight: '1rem',
    },
    searchStyle: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '0.95rem',
      width: '200px',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
     
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    cashButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#f59e0b',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
      transform: 'translateY(0)',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to access the Customer Entries</h2>
        </div>
      </div>
    );
  }

  if (showReceiptPage && selectedCustomer) {
    return <ReceiptManagementSystem initialCustomer={selectedCustomer} onBack={() => setShowReceiptPage(false)} />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Customer Entries</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onAddCustomer}
            style={styles.addButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
            }}
          >
            + Add Customer Entry
          </button>
          <button
            onClick={exportToCSV}
            style={styles.exportButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(76, 175, 80, 0.3)';
            }}
          >
            Export CSV
          </button>
          <input
            type="text"
            placeholder="Search By Customer Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchStyle}
          />
        </div>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
            <p>⏳ Loading customers...</p>
          </div>
        ) : filteredCustomers.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>S.No</th>
                <th style={styles.tableHeader}>Customer Name</th>
                <th style={styles.tableHeader}>Type</th>
                <th style={styles.tableHeader}>Address</th>
                <th style={styles.tableHeader}>City</th>
                <th style={styles.tableHeader}>Mobile No</th>
                <th style={styles.tableHeader}>GSTIN</th>
                <th style={styles.tableHeader}>Outstanding</th>
                <th style={styles.tableHeader}>Active</th>
                <th style={styles.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer, index) => (
                <tr
                  key={customer.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                  <td style={styles.tableCell}>
                    {customer.customerName || customer.name || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>{customer.type || 'N/A'}</td>
                  <td style={styles.tableCell}>
                    {`${customer.address || customer.address1 || ''} ${customer.address2 || ''}`.trim() || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>{customer.city || 'N/A'}</td>
                  <td style={styles.tableCell}>{customer.mobile || 'N/A'}</td>
                  <td style={styles.tableCell}>{customer.gstin || 'N/A'}</td>
                  <td style={styles.tableCell}>
                    <strong style={{ color: Number(customer.summary?.outstanding ?? customer.obAmount ?? 0) <= 0.01 ? '#10b981' : '#ef4444' }}>
                      ₹{Number(customer.summary?.outstanding ?? customer.obAmount ?? 0).toFixed(2)}
                    </strong>
                  </td>
                  <td style={styles.tableCell}>
                    <select
                      value={customer.visibility ? 'YES' : 'NO'}
                      onChange={(e) => handleActiveChange(customer.id, e.target.value)}
                      style={{
                        padding: '0.25rem',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </td>
                  <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                    <button
                      onClick={() => onEditCustomer(customer)}
                      style={styles.editButton}
                      title="Edit"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(customer.id)}
                      style={styles.deleteButton}
                      title="Delete"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                      }}
                    >
                      🗑️
                    </button>
                    <button
                      onClick={() => handleCashClick(customer)}
                      style={styles.cashButton}
                      title="Receive Payment"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                      }}
                    >
                      💰
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📁</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Customers Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first customer!</p>
            <button
              onClick={onAddCustomer}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Customer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerTable;